import i18nFactory from '@yandex-int/i18n';
import { keys } from 'lodash';

import { Region } from '../../typings/common';
import { DisplayPageBlockData, DisplayRegion } from '../../typings/displayMap';
import {
    BannerFormats,
    BannerSize,
    IndoorData,
    OutdoorData,
    PredictorGeo,
    ReachResultValue,
    StatusReach,
    TextPredictor
} from '../../typings/predictor';
import { getStringValueParts } from '../FormatHelpers/FormatHelpers';
import { sortRegions } from '../IndoorOutdoorHelpers/IndoorOutdoorHelpers';
import { RegionsHelpers } from '../RegionsHelpers/RegionsHelpers';

import * as keyset from './PredictorHelpers.i18n';

const i18n = i18nFactory(keyset);

export enum PredictorLoadStatus {
    EMPTY = 0,
    FULL = 100
}
const MAX_VALUE_HINT_LENGTH = 160;

export class PredictorHelpers {
    static getValueRegion(text: string): string {
        if (text.length <= MAX_VALUE_HINT_LENGTH) {
            return text;
        }

        // обрезаем по целым словам
        const result = text.slice(0, MAX_VALUE_HINT_LENGTH);

        return text.slice(0, result.lastIndexOf(', ') + 1);
    }

    static getRegion(
        regions: Region[],
        selectedMap: Record<number, boolean>
    ): PredictorGeo {
        const result: PredictorGeo = {
            ids: null,
            text: ''
        };

        const listSelectedRegions = RegionsHelpers.getSelectedRegionsInfo(
            regions,
            selectedMap
        );
        const selectedRegionsNameFormat = RegionsHelpers.convertSelectedRegionsInfoToNameFormat(
            listSelectedRegions
        );

        result.ids = RegionsHelpers.convertRegionsSelectedInfoToFlattenList(
            listSelectedRegions
        );

        if (selectedRegionsNameFormat.length > 0) {
            result.text = this.getValueRegion(selectedRegionsNameFormat);
        }

        return result;
    }

    static getListData(
        regions: Region[],
        selectedMap: Record<number, boolean>
    ): TextPredictor[] {
        const result = [];

        const geo = this.getRegion(regions, selectedMap);

        result.push({
            title: i18n('regions'),
            value: geo.text
        });

        return result;
    }

    static getListRecommendationData(
        bannersFormat?: BannerFormats[]
    ): TextPredictor[] | undefined {
        if (!bannersFormat) {
            return;
        }

        const banners = bannersFormat
            .filter(banner => banner.increasePercent > 1)
            .sort((a, b) => {
                return b.increasePercent - a.increasePercent;
            })
            .slice(0, 3);

        return banners.map(banner => {
            return {
                title: banner.format,
                value: String(banner.increasePercent.toFixed(1))
            };
        });
    }

    static getPercent(
        sum?: ReachResultValue,
        detailed?: ReachResultValue
    ): number {
        if (!sum || !detailed || detailed.status === StatusReach.LESS) {
            return PredictorLoadStatus.EMPTY;
        }

        if (
            detailed.status === StatusReach.EMPTY ||
            detailed.value === undefined ||
            sum.value === undefined
        ) {
            return PredictorLoadStatus.FULL;
        }

        return Math.ceil((detailed.value / sum.value) * 100);
    }

    static getBasedText(result?: ReachResultValue): string {
        if (!result || result.value === undefined) {
            return i18n('predictor-error');
        }

        const { value, sign } = getStringValueParts(result.value);
        const signedValue = `${value} ${sign}`;

        if (result.status === StatusReach.LESS) {
            return i18n('predictor-sum-less', { sum: signedValue });
        }

        return i18n('predictor-sum-from', { sum: signedValue });
    }

    static getDetailedText(result?: ReachResultValue): string {
        if (!result || result.value === undefined) {
            return '-';
        }

        const { value, sign } = getStringValueParts(result.value);
        const signedValue = `${value} ${sign}`;

        if (result.status === StatusReach.LESS) {
            return i18n('predictor-detailed-less', { sum: signedValue });
        }

        return signedValue;
    }

    static getBannerData(banners?: BannerSize[]): TextPredictor | undefined {
        if (banners && banners.length) {
            const bannerSizes = banners
                .map(item => {
                    const separator = item.isAspect ? ':' : 'x';

                    return item.isAdaptive
                        ? i18n('adaptive')
                        : `${item.width}${separator}${item.height}`;
                })
                .join(', ');

            return {
                title: i18n('banner-sizes'),
                value: bannerSizes
            };
        }

        return;
    }

    static checkBannerSizeIsUnique(
        banners: BannerSize[],
        bannerSize: BannerSize
    ): BannerSize | undefined {
        const {
            width,
            height,
            isAdaptive = false,
            isAspect = false
        } = bannerSize;

        const isExistBannerSize = banners.some(item => {
            return (
                item.width === width &&
                item.height === height &&
                isAdaptive === item.isAdaptive &&
                isAspect === item.isAspect
            );
        });

        if (
            !isExistBannerSize &&
            (isAdaptive || (width !== undefined && height !== undefined))
        ) {
            return {
                width: width || undefined,
                height: height || undefined,
                isAdaptive,
                isAspect
            };
        }

        return;
    }

    static getDefaultOutdoorData(): TextPredictor[] {
        return [
            {
                title: i18n('regions'),
                value: '–'
            },
            {
                title: i18n('formats'),
                value: '–'
            },
            {
                title: i18n('operators'),
                value: '–'
            }
        ];
    }

    static getDefaultIndoorData(): TextPredictor[] {
        return [
            {
                title: i18n('regions'),
                value: '–'
            },
            {
                title: i18n('categories'),
                value: '–'
            },
            {
                title: i18n('zones'),
                value: '–'
            }
        ];
    }

    static getOutdoorData(
        pageBlocks: DisplayPageBlockData[],
        facilities: string[] = [],
        operators: string[] = []
    ): TextPredictor[] {
        const result: OutdoorData = {
            region: [],
            facility: [],
            operatorName: [],
            screensCount: 0
        };
        const regions: DisplayRegion[] = [];

        pageBlocks.forEach((pageBlock: DisplayPageBlockData) => {
            keys(pageBlock).forEach(property => {
                if (
                    property === 'region' ||
                    property === 'facility' ||
                    property === 'operatorName'
                ) {
                    const propertyIsExist = result[property].some(
                        item => pageBlock[property] === item
                    );

                    if (!propertyIsExist) {
                        const block = pageBlock[property];

                        if (property === 'region' && block) {
                            regions.push({
                                text: block,
                                id: pageBlock.regionId
                            });
                            result[property].push(block);
                        } else if (block) {
                            result[property].push(block);
                        }
                    }
                }
            });

            result.screensCount = result.screensCount + pageBlock.screensCount;
        });

        const notCheckedFormats = facilities.filter(facility => {
            return !result.facility.some(item => item === facility);
        });

        const notCheckedOperators = operators.filter(operator => {
            return !result.operatorName.some(item => item === operator);
        });

        return [
            {
                title: i18n('regions'),
                value: sortRegions(regions)
                    .map(region => region.text)
                    .join(', ')
            },
            {
                title: i18n('formats'),
                value: !notCheckedFormats.length
                    ? i18n('all')
                    : result.facility.join(', ')
            },
            {
                title: i18n('operators'),
                value: !notCheckedOperators.length
                    ? i18n('all')
                    : result.operatorName.join(', ')
            },
            {
                title: i18n('screens'),
                value: !result.screensCount
                    ? '–'
                    : result.screensCount.toString()
            }
        ];
    }

    static getIndoorData(
        pageBlocks: DisplayPageBlockData[],
        facilities: string[] = [],
        zones: string[] = []
    ): TextPredictor[] {
        const result: IndoorData = {
            region: [],
            facility: [],
            zone: [],
            screensCount: 0
        };
        const regions: DisplayRegion[] = [];

        pageBlocks.forEach((pageBlock: DisplayPageBlockData) => {
            keys(pageBlock).forEach(property => {
                if (
                    property === 'region' ||
                    property === 'facility' ||
                    property === 'zone'
                ) {
                    const propertyIsExist = result[property].some(
                        item => pageBlock[property] === item
                    );

                    if (!propertyIsExist) {
                        const block = pageBlock[property];

                        if (property === 'region' && block) {
                            regions.push({
                                text: block,
                                id: pageBlock.regionId
                            });
                            result[property].push(block);
                        } else if (block) {
                            result[property].push(block);
                        }
                    }
                }
            });

            result.screensCount = result.screensCount + pageBlock.screensCount;
        });

        const notCheckedFormats = facilities.filter(facility => {
            return !result.facility.some(item => item === facility);
        });

        const notCheckedZones = zones.filter(zone => {
            return !result.zone.some(item => item === zone);
        });

        return [
            {
                title: i18n('regions'),
                value: sortRegions(regions)
                    .map(region => region.text)
                    .join(', ')
            },
            {
                title: i18n('categories'),
                value: !notCheckedFormats.length
                    ? i18n('all')
                    : result.facility.join(', ')
            },
            {
                title: i18n('zones'),
                value: !notCheckedZones.length
                    ? i18n('all')
                    : result.zone.join(', ')
            },
            {
                title: i18n('screens'),
                value: !result.screensCount
                    ? '–'
                    : result.screensCount.toString()
            }
        ];
    }
}
